'use client';

import { useEffect, useRef } from 'react';
import { bebasNeue, geistMono } from 'utils/fonts';
import {
  isIllustratedFinish,
  isPatternFinish,
  type MetalFinish,
  type VoucherFinish,
} from 'content/gifting/voucherConfig';
import {
  METAL_FS,
  METAL_VS,
  SILVER,
} from 'components/gifting/visuals/metalCardShader';
import {
  createFullScreenProgram,
  drawFullScreen,
  loseGl,
} from 'components/gifting/visuals/glProgram';
import { usePrefersReducedMotion } from 'hooks/media/usePrefersReducedMotion';
import { IllustratedCard } from './cards/IllustratedCard';
import { PatternCard } from './cards/PatternCard';
import { attachCardTilt } from './cards/cardTilt';
import type { CardProps } from './cards/types';
import styles from './VoucherCard.module.css';

type Preset = {
  dark: readonly number[];
  mid: readonly number[];
  light: readonly number[];
  fringe: number;
  strength: number;
  q0: number;
  edgeColor: readonly number[];
  edgeStrength: number;
  rim: number;
  grain: number;
};

// Light presets from the design's MC-JS (`black`, `gold`, `silver`).
const PRESETS: Record<MetalFinish, Preset> = {
  'aster-black': {
    dark: [0.045, 0.045, 0.05],
    mid: [0.125, 0.125, 0.135],
    light: [0.42, 0.42, 0.44],
    fringe: 0.2,
    strength: 0.24,
    q0: -0.02,
    edgeColor: [0.82, 0.82, 0.84],
    edgeStrength: 1.0,
    rim: 0,
    grain: 0.05,
  },
  'bionic-gold': {
    dark: [0.3, 0.19, 0.05],
    mid: [0.7, 0.54, 0.26],
    light: [0.93, 0.81, 0.52],
    fringe: 0.7,
    strength: 0.15,
    q0: -0.02,
    edgeColor: [0.9, 0.8, 0.58],
    edgeStrength: 0.8,
    rim: 0.12,
    grain: 0.03,
  },
  'space-silver': SILVER,
};

const FINISH_CLASS: Record<MetalFinish, string> = {
  'aster-black': styles.black,
  'bionic-gold': styles.gold,
  'space-silver': styles.silver,
};

/** One static frame of the metal shader per size / finish; null without WebGL. */
const createRenderer = (canvas: HTMLCanvasElement) => {
  // preserveDrawingBuffer: CardStage samples this canvas to morph between cards.
  const gl = canvas.getContext('webgl', {
    antialias: true,
    premultipliedAlpha: false,
    preserveDrawingBuffer: true,
  });
  if (!gl) return null;
  const prog = createFullScreenProgram(gl, METAL_VS, METAL_FS);
  if (!prog) {
    loseGl(gl);
    return null;
  }
  const u = (n: string) => gl.getUniformLocation(prog, n);

  const draw = (w: number, h: number, p: Preset) => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.max(2, Math.round(w * dpr));
    canvas.height = Math.max(2, Math.round(h * dpr));
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform2f(u('uRes'), canvas.width, canvas.height);
    gl.uniform1f(u('uDpr'), dpr);
    gl.uniform3fv(u('uDark'), p.dark as number[]);
    gl.uniform3fv(u('uMid'), p.mid as number[]);
    gl.uniform3fv(u('uLight'), p.light as number[]);
    gl.uniform1f(u('uFringe'), p.fringe);
    gl.uniform1f(u('uStrength'), p.strength);
    gl.uniform1f(u('uQ0'), p.q0);
    gl.uniform3fv(u('uEdgeC'), p.edgeColor as number[]);
    gl.uniform1f(u('uEdgeS'), p.edgeStrength);
    gl.uniform1f(u('uRim'), p.rim);
    gl.uniform1f(u('uGrain'), p.grain);
    gl.uniform1f(u('uLine'), 0);
    gl.uniform1f(u('uBevel'), 0);
    gl.uniform1f(u('uSa'), 1);
    gl.uniform2f(u('uTilt'), 0, 0);
    gl.uniform1f(u('uHolo'), 0);
    gl.uniform1f(u('uSw'), 0);
    drawFullScreen(gl);
  };
  const dispose = () => loseGl(gl);
  return { draw, dispose };
};

type Props<F extends VoucherFinish = VoucherFinish> = CardProps & {
  finish: F;
};

// Static metal voucher card: no animation, so nothing to gate on reduced motion.
// WebGL starts when the card nears the viewport; the CSS gradient is the fallback.
const MetalVoucherCard = ({
  finish,
  title,
  subtitle,
  label,
  amount,
  className,
}: Props<MetalFinish>) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const drawRef = useRef<(() => void) | null>(null);
  const finishRef = useRef(finish);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    let renderer: ReturnType<typeof createRenderer> = null;
    let ro: ResizeObserver | null = null;
    let canvas: HTMLCanvasElement | null = null;

    const start = () => {
      if (renderer) return;
      // A fresh canvas per mount: a context lost on unmount can't be reused.
      canvas = document.createElement('canvas');
      canvas.className = styles.gl;
      canvas.setAttribute('aria-hidden', 'true');
      renderer = createRenderer(canvas);
      if (!renderer) {
        canvas = null;
        return;
      }
      card.prepend(canvas);
      card.dataset.gl = 'on';
      const r = renderer;
      drawRef.current = () =>
        r.draw(card.clientWidth, card.clientHeight, PRESETS[finishRef.current]);
      ro = new ResizeObserver(() => drawRef.current?.());
      ro.observe(card);
      drawRef.current();
    };

    const near = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        near.disconnect();
        start();
      },
      { rootMargin: '300px 0px' },
    );
    near.observe(card);

    return () => {
      near.disconnect();
      ro?.disconnect();
      renderer?.dispose();
      canvas?.remove();
      drawRef.current = null;
      delete card.dataset.gl;
    };
  }, []);

  useEffect(() => {
    finishRef.current = finish;
    drawRef.current?.();
  }, [finish]);

  // Hover, as on the pattern cards: tilt toward the pointer, glare follows it.
  useEffect(() => {
    const card = cardRef.current;
    return card ? attachCardTilt(card, { reducedMotion }) : undefined;
  }, [reducedMotion]);

  return (
    <div
      ref={cardRef}
      className={`${styles.card} ${FINISH_CLASS[finish]} ${
        bebasNeue.variable
      } ${geistMono.variable} ${className ?? ''}`}
      role="img"
      aria-label={label}
    >
      <div className={styles.tag}>
        {amount && <span className={styles.amount}>{amount}</span>}
        <span className={styles.t1}>{title}</span>
        <span className={styles.t2}>{subtitle}</span>
      </div>
      <i className={styles.mark} aria-hidden="true">
        <b className={styles.markShadow} />
        <b className={styles.markHighlight} />
        <b className={styles.markFill} />
      </i>
    </div>
  );
};

/** Voucher card for any finish: metal, illustrated or animated pattern. */
export const VoucherCard = ({ finish, ...rest }: Props) => {
  // Keyed by finish: each renderer sets up its own WebGL context for one design.
  if (isIllustratedFinish(finish))
    return <IllustratedCard key={finish} finish={finish} {...rest} />;
  if (isPatternFinish(finish))
    return <PatternCard key={finish} finish={finish} {...rest} />;
  return <MetalVoucherCard finish={finish} {...rest} />;
};
